import { DateRangePicker, Popover, getDate } from '@systemeio/ui'
import { useContactSurveys } from 'modules/contacts/contact-page/hooks/use-contact-surveys'
import { ContactPageFilterInterface } from 'modules/contacts/contact-page/types/contact-page-filter-interface'
import {
  ContactSurveysContentInterface,
  ContactSurveysInterface,
} from 'modules/contacts/contact-page/types/contact-surveys-interface'
import { ContactId, ContactInterface } from 'modules/contacts/types/contact-interface'
import React, { useEffect } from 'react'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination'
import { OrderEnum } from 'shared/types/pagination-interface'
import { getDateRangeSinceDate, getToday } from 'shared/utils/get-dates'
import { CalendarBookingIcon } from '@systemeio/ui/icons'
import { pageTypeTitlesLocoKeys } from 'shared/enums/page-type-enum'

interface ContactSurveysProps {
  contactId: ContactId
  contact?: ContactInterface
}

const defaultFilters: ContactPageFilterInterface = { date: [null, null] }

const hasSurveyContent = (
  content: ContactSurveysInterface['content'],
): content is ContactSurveysContentInterface[] => !Array.isArray(content[0])

const ContactSurveys = ({ contactId, contact }: ContactSurveysProps) => {
  const { t } = useLocoTranslation()
  const { user, intlLocale } = useUser()
  const { setPagination, pagination, setLimit, limit, filter, setFilter } = useFilterPagination(
    defaultFilters,
    true,
  )
  const { contactSurveys } = useContactSurveys({
    contactId,
    limit,
    pagination,
    filter,
    shouldFetch: filter.date[0] !== null,
  })

  useEffect(() => {
    if (contact) {
      setFilter({ date: getDateRangeSinceDate(contact.createdAt, user?.timezone) })
    }
  }, [contact, setFilter, user?.timezone])

  const columns: ColumnDefinitionInterface<ContactSurveysInterface>[] = [
    {
      key: 'contactOptInHistoryItem',
      header: t('global.source'),
      widthPercent: 40,
      onRender: survey => (
        <div className={'flex w-full flex-col'}>
          {survey.contactOptInHistoryItem.httpReferrer && (
            <div className={'flex w-full gap-1'}>
              <span className={'whitespace-nowrap'}>{`${t(
                'dashboard.contact_page.source_url',
              )}: `}</span>
              <a
                className={'primary-link truncate outline-hidden'}
                href={survey.contactOptInHistoryItem.httpReferrer}
                target={'_blank'}
                rel={'noreferrer'}
              >
                {survey.contactOptInHistoryItem.httpReferrer}
              </a>
            </div>
          )}
          {survey.contactOptInHistoryItem.page && (
            <div className={'flex w-full gap-1'}>
              <span className={'whitespace-nowrap'}>{`${t(
                pageTypeTitlesLocoKeys[survey.contactOptInHistoryItem.page.type],
              )}: `}</span>
              <a
                className={'primary-link truncate outline-hidden'}
                href={survey.contactOptInHistoryItem.page.editUrl}
              >
                {survey.contactOptInHistoryItem.page.name}
              </a>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'content',
      header: t('dashboard.contact_page.survey_title'),
      widthPercent: 60,
      onRender: survey => (
        <div className={'flex items-start justify-between gap-1'}>
          <div className={'flex flex-col truncate'}>
            {hasSurveyContent(survey.content) &&
              survey.content.map((el, idx) => (
                <div
                  key={idx}
                  className={`flex flex-col gap-1 truncate py-3 ${
                    idx !== 0 && 'border-text-secondary/30 border-t'
                  } ${idx === 0 && 'pt-0'} ${idx + 1 === survey.content.length && 'pb-0'}`}
                >
                  <Popover
                    label={
                      <div className={'flex max-w-2xl flex-row gap-2'}>
                        {el.userAnswers.map((answer, index) =>
                          answer.openAnswer ? (
                            <>
                              <div
                                key={`${answer.title}-${index}`}
                                className={'flex flex-col items-start gap-1'}
                              >
                                <span className={'text-text-primary font-bold'}>
                                  {t('dashboard.contact_page.survey_question')}
                                </span>
                                <span key={index}>{answer.title}</span>
                              </div>
                              <div
                                key={`${answer.openAnswer}-${index}`}
                                className={'flex flex-col items-start gap-1'}
                              >
                                <span className={'text-text-primary font-bold'}>
                                  {t('dashboard.contact_page.survey_answers')}
                                </span>
                                <span>{answer.openAnswer}</span>
                              </div>
                            </>
                          ) : (
                            <div
                              key={`${answer.title}-${index}`}
                              className={'flex flex-col items-start gap-1'}
                            >
                              <span className={'text-text-primary font-bold'}>
                                {t('dashboard.contact_page.survey_answers')}
                              </span>
                              <span>{answer.title}</span>
                            </div>
                          ),
                        )}
                        {el.tags.length !== 0 && (
                          <div className={'flex flex-col items-start gap-1'}>
                            <span className={'text-text-primary font-bold'}>
                              {t('dashboard.contact_page.tag')}
                            </span>
                            {el.tags.map((tag, index) =>
                              tag.id === null ? (
                                <span key={index}>{tag.name}</span>
                              ) : (
                                <LinkWithoutPrefetch
                                  key={index}
                                  className="primary-link w-full"
                                  href={`/contacts?filter=%5B%7B"searchValue"%3A"${tag.id}"%2C"compareOperation"%3A"IN_LIST"%2C"searchField"%3A"tag"%7D%5D`}
                                >
                                  {tag.name}
                                </LinkWithoutPrefetch>
                              ),
                            )}
                          </div>
                        )}
                      </div>
                    }
                  >
                    {open => (
                      <span
                        className={`group-focus-visible:text-text-secondary text-start w-full whitespace-normal ${
                          open ? 'text-text-secondary' : 'text-brand-primary'
                        }`}
                      >
                        {el.title}
                      </span>
                    )}
                  </Popover>
                </div>
              ))}
          </div>
          {survey.contactOptInHistoryItem.optInBookingData && (
            <Popover label={t('dashboard.contact_page.survey_booking_popover')}>
              {open => (
                <CalendarBookingIcon
                  className={`h-4 w-4 ${
                    open ? 'fill-brand-primary' : 'fill-text-primary'
                  } main-transition-colors`}
                />
              )}
            </Popover>
          )}
        </div>
      ),
    },
  ]
  return (
    <ListingLayout
      counter={false}
      data={contactSurveys}
      type={'default'}
      title={
        <span className={'text-[17px]'}>{t('dashboard.contact_page.survey_title_plural')}</span>
      }
      customHeaderElement={
        <DateRangePicker
          maxDate={getToday(user?.timezone)}
          minDate={getDate(contact?.createdAt, user?.timezone)}
          getDefaultDate={() => getDateRangeSinceDate(contact?.createdAt, user?.timezone)}
          date={filter.date}
          onChange={date => {
            setPagination({ startFromId: undefined, order: OrderEnum.next })
            setLimit(10)
            setFilter({ date: date })
          }}
          timezone={user?.timezone}
          createdAt={user?.createdAt}
          intlLocale={intlLocale}
        />
      }
    >
      <Table
        limiter={{ limit, setLimit }}
        paginator={{ pagination, setPagination }}
        data={contactSurveys}
        columns={columns}
      />
    </ListingLayout>
  )
}

export default ContactSurveys
